import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import { rememberOAuthReferral, startGoogleAuth } from '@/features/auth/googleAuth';
import { markPasswordRecoveryPending } from '@/features/auth/passwordRecovery';
import { trackConversion } from '@/features/analytics/tracker';
import { getOAuthRedirectUrl } from '@/lib/nativeApp';
import { kuunda, kuundaErrorMessage } from '@/lib/kuunda';
import '../marketing/marketing.css';

type CountryOption = { iso2: string; name: string; dial: string };

// Liste des pays africains avec indicatifs téléphoniques.
// Le backend attend `country_code` en ISO2 (ex. "CI" pour la Côte d’Ivoire).
const AFRICAN_COUNTRIES: CountryOption[] = [
  { iso2: 'DZ', name: 'Algérie', dial: '213' },
  { iso2: 'AO', name: 'Angola', dial: '244' },
  { iso2: 'BJ', name: 'Bénin', dial: '229' },
  { iso2: 'BW', name: 'Botswana', dial: '267' },
  { iso2: 'BF', name: 'Burkina Faso', dial: '226' },
  { iso2: 'BI', name: 'Burundi', dial: '257' },
  { iso2: 'CV', name: 'Cap-Vert', dial: '238' },
  { iso2: 'CM', name: 'Cameroun', dial: '237' },
  { iso2: 'CF', name: 'République centrafricaine', dial: '236' },
  { iso2: 'TD', name: 'Tchad', dial: '235' },
  { iso2: 'KM', name: 'Comores', dial: '269' },
  { iso2: 'CG', name: 'Congo', dial: '242' },
  { iso2: 'CD', name: 'République démocratique du Congo', dial: '243' },
  { iso2: 'CI', name: 'Côte d’Ivoire', dial: '225' },
  { iso2: 'DJ', name: 'Djibouti', dial: '253' },
  { iso2: 'EG', name: 'Égypte', dial: '20' },
  { iso2: 'GQ', name: 'Guinée équatoriale', dial: '240' },
  { iso2: 'ER', name: 'Érythrée', dial: '291' },
  { iso2: 'SZ', name: 'Eswatini', dial: '268' },
  { iso2: 'ET', name: 'Éthiopie', dial: '251' },
  { iso2: 'GA', name: 'Gabon', dial: '241' },
  { iso2: 'GM', name: 'Gambie', dial: '220' },
  { iso2: 'GH', name: 'Ghana', dial: '233' },
  { iso2: 'GN', name: 'Guinée', dial: '224' },
  { iso2: 'GW', name: 'Guinée-Bissau', dial: '245' },
  { iso2: 'KE', name: 'Kenya', dial: '254' },
  { iso2: 'LS', name: 'Lesotho', dial: '266' },
  { iso2: 'LR', name: 'Libéria', dial: '231' },
  { iso2: 'LY', name: 'Libye', dial: '218' },
  { iso2: 'MG', name: 'Madagascar', dial: '261' },
  { iso2: 'MW', name: 'Malawi', dial: '265' },
  { iso2: 'ML', name: 'Mali', dial: '223' },
  { iso2: 'MR', name: 'Mauritanie', dial: '222' },
  { iso2: 'MU', name: 'Maurice', dial: '230' },
  { iso2: 'MA', name: 'Maroc', dial: '212' },
  { iso2: 'MZ', name: 'Mozambique', dial: '258' },
  { iso2: 'NA', name: 'Namibie', dial: '264' },
  { iso2: 'NE', name: 'Niger', dial: '227' },
  { iso2: 'NG', name: 'Nigeria', dial: '234' },
  { iso2: 'RW', name: 'Rwanda', dial: '250' },
  { iso2: 'ST', name: 'Sao Tomé-et-Principe', dial: '239' },
  { iso2: 'SN', name: 'Sénégal', dial: '221' },
  { iso2: 'SC', name: 'Seychelles', dial: '248' },
  { iso2: 'SL', name: 'Sierra Leone', dial: '232' },
  { iso2: 'SO', name: 'Somalie', dial: '252' },
  { iso2: 'ZA', name: 'Afrique du Sud', dial: '27' },
  { iso2: 'SS', name: 'Soudan du Sud', dial: '211' },
  { iso2: 'SD', name: 'Soudan', dial: '249' },
  { iso2: 'TZ', name: 'Tanzanie', dial: '255' },
  { iso2: 'TG', name: 'Togo', dial: '228' },
  { iso2: 'TN', name: 'Tunisie', dial: '216' },
  { iso2: 'UG', name: 'Ouganda', dial: '256' },
  { iso2: 'ZM', name: 'Zambie', dial: '260' },
  { iso2: 'ZW', name: 'Zimbabwe', dial: '263' },
];

function EyeIcon({ visible }: { visible: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden style={{ display: 'block' }}>
      <path
        fill="currentColor"
        d="M12 5c-7.633 0-10.5 7-10.5 7S4.367 19 12 19s10.5-7 10.5-7S19.633 5 12 5Zm0 12a5 5 0 1 1 0-10a5 5 0 0 1 0 10Zm0-8a3 3 0 1 0 0 6a3 3 0 0 0 0-6Z"
      />
      {!visible ? (
        <path
          fill="currentColor"
          d="M2.1 2.1a1 1 0 0 1 1.4 0l17.0 17.0a1 1 0 1 1-1.4 1.4l-17.0-17.0a1 1 0 0 1 0-1.4Z"
        />
      ) : null}
    </svg>
  );
}

function PasswordInput({
  id,
  label,
  value,
  onChange,
  required,
  minLength,
  visible,
  onToggle,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  minLength?: number;
  visible: boolean;
  onToggle: () => void;
  autoComplete?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="field-password">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          required={required}
          minLength={minLength}
          value={value}
          autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="field-password-toggle"
          onClick={onToggle}
          aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        >
          <EyeIcon visible={visible} />
        </button>
      </div>
    </div>
  );
}

function GoogleButton({
  loading,
  onClick,
  label,
}: {
  loading: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button type="button" className="btn btn-google btn-block" disabled={loading} onClick={onClick}>
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
        <path
          fill="#FFC107"
          d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
        />
        <path
          fill="#FF3D00"
          d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.1 29.3 4 24 4 16.1 4 9.2 8.5 6.3 14.7z"
        />
        <path
          fill="#4CAF50"
          d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.3 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.1 39.5 16 44 24 44z"
        />
        <path
          fill="#1976D2"
          d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l.1.1 6.2 5.2C40.5 35.4 44 30.3 44 24c0-1.2-.1-2.3-.4-3.5z"
        />
      </svg>
      {loading ? 'Redirection…' : label}
    </button>
  );
}

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const session = await signIn(email.trim(), password);
      const { data } = await kuunda.from('profiles').select('role').eq('id', session.user.id).maybeSingle();
      const role = data?.role;
      navigate(role === 'admin' || role === 'advisor' ? '/admin' : '/app', { replace: true });
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Connexion impossible';
      if (/email not confirmed/i.test(raw)) {
        setError(
          'Votre e-mail n’est pas encore confirmé. Ouvrez le lien reçu par e-mail (idéalement dans le même navigateur), puis reconnectez-vous.',
        );
      } else if (/invalid login credentials/i.test(raw)) {
        setError('Email ou mot de passe incorrect.');
      } else {
        setError(kuundaErrorMessage(err, 'Connexion impossible'));
      }
    } finally {
      setLoading(false);
    }
  }

  async function onGoogle() {
    setError('');
    setLoading(true);
    rememberOAuthReferral(undefined);
    const { error: err } = await startGoogleAuth();
    if (err) {
      setError(kuundaErrorMessage(err, 'Connexion Google impossible'));
      setLoading(false);
    }
  }

  return (
    <div className="mkt mkt-auth">
      <aside className="mkt-auth-visual" aria-hidden>
        <img src="/images/kilimanjaro.jpg" alt="" />
        <div className="mkt-auth-visual-copy">
          <p>De l’idée à l’entreprise durable en Afrique</p>
        </div>
      </aside>
      <div className="mkt-auth-panel">
        <div className="mkt-auth-card">
          <Link to="/" className="mkt-auth-brand" aria-label="ISBD — accueil">
            <img src="/logo-isbd.png" alt="ISBD" />
          </Link>
          <header className="mkt-auth-head">
            <h1>Connexion</h1>
            <p>Accédez à votre espace entrepreneur.</p>
          </header>
          {error ? <div className="error-banner">{error}</div> : null}
          <GoogleButton loading={loading} onClick={() => void onGoogle()} label="Continuer avec Google" />
          <div className="auth-divider" role="separator">
            <span>ou</span>
          </div>
          <form className="mkt-auth-form" onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <PasswordInput
              id="password"
              label="Mot de passe"
              value={password}
              onChange={setPassword}
              required
              minLength={10}
              visible={showPassword}
              onToggle={() => setShowPassword((s) => !s)}
              autoComplete="current-password"
            />
            <p className="mkt-auth-forgot">
              <Link to="/mot-de-passe-oublie">Mot de passe oublié ?</Link>
            </p>
            <button className="btn btn-primary btn-block" type="submit" disabled={loading}>
              {loading ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>
          <p className="mkt-auth-switch">
            Pas encore de compte ? <Link to="/inscription">S’inscrire</Link>
          </p>
          <p className="mkt-auth-legal muted">
            <Link to="/politique-de-confidentialite">Confidentialité</Link>
            {' · '}
            <Link to="/conditions-d-utilisation">Conditions</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: 'CI',
    password: '',
    passwordConfirm: '',
    referralToken: params.get('ref') ?? '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setOk('');
    if (form.password !== form.passwordConfirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (form.password.length < 10) {
      setError('Le mot de passe doit contenir au moins 10 caractères.');
      return;
    }
    setLoading(true);
    try {
      await signUp({
        email: form.email.trim(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        countryCode: form.countryCode,
        referralToken: form.referralToken.trim() || undefined,
      });
      trackConversion('signup', { has_referral: Boolean(form.referralToken.trim()) }, '/inscription');
      setOk('Compte créé. Vérifiez votre email si la confirmation est activée, puis connectez-vous.');
      setTimeout(() => navigate('/connexion'), 1200);
    } catch (err) {
      setError(kuundaErrorMessage(err, 'Inscription impossible'));
    } finally {
      setLoading(false);
    }
  }

  async function onGoogle() {
    setError('');
    setOk('');
    setLoading(true);
    rememberOAuthReferral(form.referralToken);
    const { error: err } = await startGoogleAuth();
    if (err) {
      setError(kuundaErrorMessage(err, 'Connexion Google impossible'));
      setLoading(false);
    }
  }

  return (
    <div className="mkt mkt-auth">
      <aside className="mkt-auth-visual mkt-auth-visual-inscription" aria-hidden>
        <img src="/images/auth-inscription.jpg" alt="" />
        <div className="mkt-auth-visual-copy">
          <p>Rejoignez la communauté des entrepreneurs ISBD</p>
        </div>
      </aside>
      <div className="mkt-auth-panel">
        <div className="mkt-auth-card">
          <Link to="/" className="mkt-auth-brand" aria-label="ISBD — accueil">
            <img src="/logo-isbd.png" alt="ISBD" />
          </Link>
          <header className="mkt-auth-head">
            <h1>Inscription</h1>
            <p>Créez votre compte entrepreneur en quelques minutes.</p>
          </header>
          {error ? <div className="error-banner">{error}</div> : null}
          {ok ? <div className="success-banner">{ok}</div> : null}
          <GoogleButton loading={loading} onClick={() => void onGoogle()} label="S’inscrire avec Google" />
          <div className="auth-divider" role="separator">
            <span>ou</span>
          </div>
          <form className="mkt-auth-form" onSubmit={onSubmit}>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="register-first">Prénom</label>
                <input
                  id="register-first"
                  required
                  autoComplete="given-name"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="register-last">Nom</label>
                <input
                  id="register-last"
                  required
                  autoComplete="family-name"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="register-email">Email</label>
              <input
                id="register-email"
                type="email"
                required
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="register-phone">Téléphone</label>
                <input
                  id="register-phone"
                  autoComplete="tel"
                  value={form.phoneNumber}
                  onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="register-country">Pays</label>
                <select
                  id="register-country"
                  required
                  value={form.countryCode}
                  onChange={(e) => setForm({ ...form, countryCode: e.target.value.toUpperCase() })}
                >
                  {AFRICAN_COUNTRIES.map((c) => (
                    <option key={c.iso2} value={c.iso2}>
                      {c.name} (+{c.dial})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid-2">
              <PasswordInput
                id="register-password"
                label="Mot de passe"
                value={form.password}
                onChange={(next) => setForm({ ...form, password: next })}
                required
                minLength={10}
                visible={showPassword}
                onToggle={() => setShowPassword((s) => !s)}
                autoComplete="new-password"
              />
              <PasswordInput
                id="register-password-confirm"
                label="Confirmation"
                value={form.passwordConfirm}
                onChange={(next) => setForm({ ...form, passwordConfirm: next })}
                required
                minLength={10}
                visible={showPasswordConfirm}
                onToggle={() => setShowPasswordConfirm((s) => !s)}
                autoComplete="new-password"
              />
            </div>
            <div className="field">
              <label htmlFor="register-referral">Code parrainage (optionnel)</label>
              <input
                id="register-referral"
                placeholder="Ex. KONAN-0042"
                value={form.referralToken}
                onChange={(e) => setForm({ ...form, referralToken: e.target.value })}
              />
            </div>
            <p className="muted mkt-auth-legal-inline">
              En créant un compte, vous acceptez les{' '}
              <Link to="/conditions-d-utilisation">conditions d’utilisation</Link> et la{' '}
              <Link to="/politique-de-confidentialite">politique de confidentialité</Link>.
            </p>
            <button className="btn btn-accent btn-block" type="submit" disabled={loading}>
              {loading ? 'Création…' : 'Créer mon compte'}
            </button>
          </form>
          <p className="mkt-auth-switch">
            Déjà membre ? <Link to="/connexion">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setOk('');
    setLoading(true);
    try {
      markPasswordRecoveryPending();
      const redirectTo = getOAuthRedirectUrl();
      const emailNorm = email.trim().toLowerCase();

      const { data, error: fnErr } = await kuunda.functions.invoke<{
        ok?: boolean;
        error?: string;
        message?: string;
      }>('auth-send-recovery', {
        body: { email: emailNorm, redirectTo },
      });

      if (fnErr) throw new Error(fnErr.message);
      if (data?.error) throw new Error(data.error);
      setOk(
        'Si un compte existe pour cette adresse, un e-mail de réinitialisation vient d’être envoyé. Vérifiez votre boîte de réception et vos spams.',
      );
    } catch (err) {
      setError(kuundaErrorMessage(err, 'Envoi impossible'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mkt mkt-auth">
      <aside className="mkt-auth-visual" aria-hidden>
        <img src="/images/kilimanjaro.jpg" alt="" />
        <div className="mkt-auth-visual-copy">
          <p>De l’idée à l’entreprise durable en Afrique</p>
        </div>
      </aside>
      <div className="mkt-auth-panel">
        <div className="mkt-auth-card">
          <Link to="/" className="mkt-auth-brand" aria-label="ISBD — accueil">
            <img src="/logo-isbd.png" alt="ISBD" />
          </Link>
          <header className="mkt-auth-head">
            <h1>Mot de passe oublié</h1>
            <p>Indiquez votre e-mail pour recevoir un lien de réinitialisation.</p>
          </header>
          {error ? <div className="error-banner">{error}</div> : null}
          {ok ? <div className="success-banner">{ok}</div> : null}
          <form className="mkt-auth-form" onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="forgot-email">Email</label>
              <input
                id="forgot-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button className="btn btn-primary btn-block" type="submit" disabled={loading}>
              {loading ? 'Envoi…' : 'Envoyer le lien'}
            </button>
          </form>
          <p className="mkt-auth-switch">
            <Link to="/connexion">← Retour à la connexion</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setOk('');
    if (password.length < 10) {
      setError('Le mot de passe doit contenir au moins 10 caractères.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      const { error: err } = await kuunda.auth.updateUser({ password });
      if (err) throw err;
      setOk('Mot de passe mis à jour. Redirection…');
      const dest = isStaff ? '/admin' : '/app';
      setTimeout(() => navigate(dest, { replace: true }), 900);
    } catch (err) {
      setError(kuundaErrorMessage(err, 'Mise à jour impossible'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mkt mkt-auth">
      <aside className="mkt-auth-visual" aria-hidden>
        <img src="/images/kilimanjaro.jpg" alt="" />
        <div className="mkt-auth-visual-copy">
          <p>De l’idée à l’entreprise durable en Afrique</p>
        </div>
      </aside>
      <div className="mkt-auth-panel">
        <div className="mkt-auth-card">
          <Link to="/" className="mkt-auth-brand" aria-label="ISBD — accueil">
            <img src="/logo-isbd.png" alt="ISBD" />
          </Link>
          <header className="mkt-auth-head">
            <h1>Nouveau mot de passe</h1>
            <p>Choisissez un mot de passe d’au moins 10 caractères.</p>
          </header>
          {error ? <div className="error-banner">{error}</div> : null}
          {ok ? <div className="success-banner">{ok}</div> : null}
          <form className="mkt-auth-form" onSubmit={onSubmit}>
            <PasswordInput
              id="reset-password"
              label="Nouveau mot de passe"
              value={password}
              onChange={setPassword}
              required
              minLength={10}
              visible={showPassword}
              onToggle={() => setShowPassword((s) => !s)}
              autoComplete="new-password"
            />
            <PasswordInput
              id="reset-password-confirm"
              label="Confirmation"
              value={passwordConfirm}
              onChange={setPasswordConfirm}
              required
              minLength={10}
              visible={showPasswordConfirm}
              onToggle={() => setShowPasswordConfirm((s) => !s)}
              autoComplete="new-password"
            />
            <button className="btn btn-primary btn-block" type="submit" disabled={loading || Boolean(ok)}>
              {loading ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
